<script setup lang="ts">
import { ref, onMounted, nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useStore } from 'vuex'
import axios from 'axios'
import { useSignUp } from '@/composables/useSignUp'
import { toast } from '@/components/ui/toast/use-toast'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Form, FormField, FormItem, FormControl, FormLabel, FormMessage } from '@/components/ui/form'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog'
import { toTypedSchema } from '@vee-validate/zod'
import { z } from 'zod'
import PhoneAuthForm from './components/PhoneAuthForm.vue'
import { Mail, Phone } from 'lucide-vue-next'

const props = withDefaults(defineProps<{ localToggle?: boolean }>(), { localToggle: false })
const emit = defineEmits<{ 'toggle-to-signin': [] }>()

const router = useRouter()
const route = useRoute()
const store = useStore()
const { signUpWithEmail, signUpWithGoogle, signUpWithGoogleForInvite, signUpForInvite, isLoading, error, clearError } = useSignUp()

const signUpSuccess = ref(false)
const showGoogleDialog = ref(false)
const showEmailForm = ref(false)
const showPhoneForm = ref(false)
const googlePending = ref(false)
const passwordInput = ref<{ $el: HTMLInputElement } | null>(null)

const handleEmailChoice = async () => {
  showEmailForm.value = true
  if (isInviteMode.value) {
    await nextTick()
    passwordInput.value?.$el?.focus()
  }
}

const handleGoogleInviteSignUp = async () => {
  clearError()
  googlePending.value = true
  const result = await signUpWithGoogleForInvite()
  googlePending.value = false
  if (result) {
    await store.dispatch('user/handleAuthentication', { firebaseToken: result.token })
    router.push({ name: 'AuthOverview' })
  } else if (error.value) {
    toast({ title: 'Sign-up failed', description: error.value, variant: 'destructive' })
  }
}

const handlePhoneSuccess = () => {
  router.push('/signin')
}

// Invite mode state
const isInviteMode = ref(false)
const inviteLoaded = ref(false)
const inviteRedirectPath = ref<string | null>(null)
const prefillEmail = ref('')
const signedUpEmail = ref('')
const inviteIsEmail = ref(false)
const prefillDisplayName = ref('')
const inviteProjectName = ref('')
const inviteInviterName = ref('')
const inviteRole = ref('')

onMounted(async () => {
  const redirect = route.query.redirect as string | undefined
  const orgInviteId = route.query.oi as string | undefined

  if (orgInviteId) {
    isInviteMode.value = true
    inviteRedirectPath.value = null
    try {
      const res = await axios.get(`/api/3/organization/invites/${orgInviteId}/preview`)
      const inv = res.data
      prefillEmail.value = inv.email || ''
      prefillDisplayName.value = inv.fullname || `${inv.first_name || ''} ${inv.last_name || ''}`.trim()
      inviteProjectName.value = inv.organization_name || ''
      inviteInviterName.value = inv.inviter_name || ''
      inviteRole.value = inv.role || ''
    } catch {
      isInviteMode.value = false
      toast({ title: 'Invite not found', description: 'This invite link is invalid or has expired.', variant: 'destructive' })
    }
    inviteLoaded.value = true
  } else if (redirect && redirect.startsWith('/invite/')) {
    isInviteMode.value = true
    inviteRedirectPath.value = redirect
    const inviteCode = redirect.replace('/invite/', '')
    try {
      const res = await axios.get(`/api/3/invite/${inviteCode}`)
      const inv = res.data
      prefillEmail.value = inv.email || ''
      inviteIsEmail.value = inv.is_email || !!inv.email
      prefillDisplayName.value = inv.fullname || `${inv.first_name || ''} ${inv.last_name || ''}`.trim()
      inviteProjectName.value = inv.project_name || ''
      inviteInviterName.value = inv.inviter_name || ''
    } catch {
      isInviteMode.value = false
      toast({ title: 'Invite not found', description: 'This invite link is invalid or has expired.', variant: 'destructive' })
    }
    inviteLoaded.value = true
  }
})

const inviteSignUpSchema = toTypedSchema(
  z.object({
    password: z.string().min(12, 'At least 12 characters')
  })
)

const signUpSchema = toTypedSchema(
  z.object({
    email: z.string().email('Invalid email address'),
    password: z.string().min(12, 'At least 12 characters'),
    displayName: z.string().min(1, 'Display name is required'),
    organizationName: z.string().min(1, 'Organization name is required'),
    projectName: z.string().min(1, 'Project name is required')
  })
)

const handleInviteSignUp = async (values: any) => {
  clearError()
  const result = await signUpForInvite({
    email: prefillEmail.value,
    password: values.password,
    displayName: prefillDisplayName.value,
  })

  if (result) {
    signUpSuccess.value = true
    toast({ title: 'Account created!', description: "Sign in to continue." })
  } else if (error.value) {
    toast({ title: 'Sign-up failed', description: error.value, variant: 'destructive' })
  }
}

const handleEmailSignUp = async (values: any) => {
  clearError()
  const result = await signUpWithEmail({
    email: values.email,
    password: values.password,
    displayName: values.displayName,
    organizationName: values.organizationName,
    projectName: values.projectName
  })

  if (result) {
    signUpSuccess.value = true
    signedUpEmail.value = values.email
    toast({ title: 'Account created!', description: 'Please check your email to verify your address.' })
  } else if (error.value) {
    toast({ title: 'Sign-up failed', description: error.value, variant: 'destructive' })
  }
}

const googleSignUpSchema = toTypedSchema(
  z.object({
    displayName: z.string().min(1, 'Display name is required'),
    organizationName: z.string().min(1, 'Organization name is required'),
    projectName: z.string().min(1, 'Project name is required')
  })
)

const handleGoogleSignUpSubmit = async (values: any) => {
  clearError()
  googlePending.value = true
  const result = await signUpWithGoogle({
    displayName: values.displayName,
    organizationName: values.organizationName,
    projectName: values.projectName
  })
  googlePending.value = false
  if (result) {
    showGoogleDialog.value = false
    toast({ title: 'Account created!', description: 'Welcome to Virunga!' })
    router.push('/signin')
  } else if (error.value) {
    toast({ title: 'Google sign-up failed', description: error.value, variant: 'destructive' })
  }
}

const goToSignIn = () => {
  if (props.localToggle) { emit('toggle-to-signin'); return }
  const email = signedUpEmail.value || prefillEmail.value || undefined
  const query: Record<string, string> = {}
  if (inviteRedirectPath.value) query.redirect = inviteRedirectPath.value
  if (email) query.email = email
  else if (inviteIsEmail.value) query.method = 'email'
  router.push({ path: '/signin', query })
}
</script>

<template>
  <div class="space-y-6 py-6">
    <!-- Header -->
    <div class="text-center space-y-2">
      <h1 class="text-3xl font-bold tracking-tight text-foreground">
        <template v-if="isInviteMode && prefillDisplayName">Hello {{ prefillDisplayName }}</template>
        <template v-else>Create Account</template>
      </h1>
      <p v-if="isInviteMode && inviteProjectName" class="text-base text-muted-foreground">
        <template v-if="inviteInviterName"><strong>{{ inviteInviterName }}</strong> invited you</template>
        <template v-else>You've been invited</template>
        to join <strong>{{ inviteProjectName }}</strong>
        <span v-if="inviteRole === 'org_admin'"> as an <strong>Administrator</strong></span>
      </p>
    </div>

    <Alert v-if="error" variant="destructive">
      <AlertDescription>{{ error }}</AlertDescription>
    </Alert>

    <!-- Success -->
    <div v-if="signUpSuccess" class="space-y-4">
      <div v-if="isInviteMode" class="text-center space-y-2">
        <h2 class="text-xl font-semibold text-foreground">You're all set!</h2>
        <p class="text-sm text-muted-foreground">
          Your account has been created. Sign in to join
          <strong>{{ inviteProjectName }}</strong><span v-if="inviteRole === 'org_admin'"> as an Administrator</span>.
        </p>
      </div>
      <Alert v-else>
        <AlertDescription>
          Account created!
        </AlertDescription>
      </Alert>
      <Button class="w-full" @click="goToSignIn">Sign In</Button>
    </div>

    <!-- Invite-only gate -->
    <div v-else-if="!isInviteMode" class="space-y-4 py-4 text-center">
      <p class="text-md text-muted-foreground leading-relaxed">
        Sign up is by invitation. Contact your administrator, or email us at <a href="mailto:hello@earthpbc.com" class="underline hover:text-foreground">hello@earthpbc.com</a> to get started.
      </p>
    </div>

    <!-- Choice screen -->
    <div v-else-if="!showEmailForm && !showPhoneForm" class="space-y-3 py-4 px-6">
      <p v-if="googlePending" class="text-center text-sm text-green-600 animate-pulse mb-5">
        Waiting for Google sign-in&hellip;
      </p>

      <!-- Google: invite → direct; regular → dialog -->
      <Button v-if="isInviteMode"
        variant="outline" class="w-full !justify-start pl-[15%]" :disabled="isLoading"
        @click="handleGoogleInviteSignUp"
      >
        <span class="w-10 flex justify-center shrink-0">
          <svg class="w-5 h-5" viewBox="0 0 24 24">
            <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
        </span>
        Continue with Google
      </Button>

      <Dialog v-else v-model:open="showGoogleDialog">
        <DialogTrigger as-child>
          <Button variant="outline" class="w-full !justify-start pl-[15%]" :disabled="isLoading">
            <span class="w-10 flex justify-center shrink-0">
              <svg class="w-5 h-5" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
            </span>
            Continue with Google
          </Button>
        </DialogTrigger>
        <DialogContent class="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Complete Your Profile</DialogTitle>
            <DialogDescription>A few details to set up your account</DialogDescription>
          </DialogHeader>
          <Form v-slot="{ meta, submitCount }" :validation-schema="googleSignUpSchema"
            :initial-values="{ displayName: '', organizationName: '', projectName: '' }"
            @submit="handleGoogleSignUpSubmit">
            <div class="space-y-4">
              <FormField v-slot="{ componentField }" :validate-on-blur="false" :validate-on-change="false" :validate-on-model-update="submitCount > 0" name="displayName">
                <FormItem><FormLabel>Display Name</FormLabel><FormControl><Input placeholder="John Doe" v-bind="componentField" /></FormControl><FormMessage /></FormItem>
              </FormField>
              <FormField v-slot="{ componentField }" :validate-on-blur="false" :validate-on-change="false" :validate-on-model-update="submitCount > 0" name="organizationName">
                <FormItem><FormLabel>Organization Name</FormLabel><FormControl><Input placeholder="Acme Corp" v-bind="componentField" /></FormControl><FormMessage /></FormItem>
              </FormField>
              <FormField v-slot="{ componentField }" :validate-on-blur="false" :validate-on-change="false" :validate-on-model-update="submitCount > 0" name="projectName">
                <FormItem><FormLabel>Project Name</FormLabel><FormControl><Input placeholder="My First Project" v-bind="componentField" /></FormControl><FormMessage /></FormItem>
              </FormField>
              <Button type="submit" class="w-full " :disabled="isLoading || !meta.valid">
                {{ isLoading ? 'Creating Account...' : 'Continue' }}
              </Button>
            </div>
          </Form>
        </DialogContent>
      </Dialog>

      <Button variant="outline" class="w-full !justify-start pl-[15%]" @click="handleEmailChoice" :disabled="isLoading">
        <span class="w-10 flex justify-center shrink-0"><Mail class="w-5 h-5" /></span>
        Continue with Email
      </Button>

      <!-- v-if guard intentional: phone invites not yet live, remove when ready -->
      <Button v-if="!isInviteMode" variant="outline" class="w-full !justify-start pl-[15%]" @click="showPhoneForm = true">
        <span class="w-10 flex justify-center shrink-0"><Phone class="w-5 h-5" /></span>
        Continue with Phone
      </Button>
    </div>

    <!-- Phone form (regular only) -->
    <div v-else-if="showPhoneForm" class="space-y-4">
      <Button variant="ghost" size="sm" class="-ml-2 text-muted-foreground" @click="showPhoneForm = false">
        ← Back
      </Button>
      <PhoneAuthForm @success="handlePhoneSuccess" @error="(msg) => toast({ title: 'Error', description: msg, variant: 'destructive' })" />
    </div>

    <!-- Email form -->
    <div v-else class="space-y-4">
      <template v-if="isInviteMode">
        <div v-if="!inviteLoaded" class="text-center py-4 text-sm text-gray-400">Loading...</div>
        <Form v-else v-slot="{ submitCount }"
          :validation-schema="inviteSignUpSchema"
          :initial-values="{ password: '' }"
          @submit="handleInviteSignUp"
        >
          <div class="space-y-4">
            <div>
              <p class="text-sm font-medium text-foreground mb-1">Email</p>
              <p class="text-sm text-muted-foreground truncate">{{ prefillEmail }}</p>
            </div>
            <FormField v-slot="{ componentField }" :validate-on-blur="false" :validate-on-change="false" :validate-on-model-update="submitCount > 0" name="password">
              <FormItem><FormLabel>Password</FormLabel><FormControl>
                <Input ref="passwordInput" type="password" autocomplete="new-password" placeholder="At least 12 characters" v-bind="componentField" />
              </FormControl><FormMessage /></FormItem>
            </FormField>
            <Button type="submit" class="w-full " :disabled="isLoading">
              {{ isLoading ? 'Creating Account...' : 'Create Account' }}
            </Button>
            <Button variant="ghost" size="sm" class="-ml-2 text-muted-foreground" @click="showEmailForm = false">
              ← Back
            </Button>
          </div>
        </Form>
      </template>

      <template v-else>
        <Form v-slot="{ meta, submitCount }"
          :validation-schema="signUpSchema"
          :initial-values="{ email: '', password: '', displayName: '', organizationName: '', projectName: '' }"
          @submit="handleEmailSignUp"
        >
          <div class="space-y-4">
            <FormField v-slot="{ componentField }" :validate-on-blur="false" :validate-on-change="false" :validate-on-model-update="submitCount > 0" name="email">
              <FormItem><FormLabel>Email</FormLabel><FormControl>
                <Input type="email" placeholder="you@example.com" v-bind="componentField" />
              </FormControl><FormMessage /></FormItem>
            </FormField>
            <FormField v-slot="{ componentField }" :validate-on-blur="false" :validate-on-change="false" :validate-on-model-update="submitCount > 0" name="password">
              <FormItem><FormLabel>Password</FormLabel><FormControl>
                <Input type="password" placeholder="At least 12 characters" v-bind="componentField" />
              </FormControl><FormMessage /></FormItem>
            </FormField>
            <FormField v-slot="{ componentField }" :validate-on-blur="false" :validate-on-change="false" :validate-on-model-update="submitCount > 0" name="displayName">
              <FormItem><FormLabel>Display Name</FormLabel><FormControl>
                <Input placeholder="John Doe" v-bind="componentField" />
              </FormControl><FormMessage /></FormItem>
            </FormField>
            <FormField v-slot="{ componentField }" :validate-on-blur="false" :validate-on-change="false" :validate-on-model-update="submitCount > 0" name="organizationName">
              <FormItem><FormLabel>Organization Name</FormLabel><FormControl>
                <Input placeholder="Acme Corp" v-bind="componentField" />
              </FormControl><FormMessage /></FormItem>
            </FormField>
            <FormField v-slot="{ componentField }" :validate-on-blur="false" :validate-on-change="false" :validate-on-model-update="submitCount > 0" name="projectName">
              <FormItem><FormLabel>Project Name</FormLabel><FormControl>
                <Input placeholder="My First Project" v-bind="componentField" />
              </FormControl><FormMessage /></FormItem>
            </FormField>
            <Button type="submit" class="w-full " :disabled="isLoading || !meta.valid">
              {{ isLoading ? 'Creating Account...' : 'Create Account' }}
            </Button>
            <Button variant="ghost" size="sm" class="-ml-2 text-muted-foreground" @click="showEmailForm = false">
              ← Back
            </Button>
          </div>
        </Form>
      </template>
    </div>

    <!-- Legal -->
    <div v-if="!signUpSuccess && isInviteMode" class="text-center">
      <p class="text-sm text-muted-foreground leading-relaxed">
        By creating an account, you agree to our
        <a href="https://www.earthpbc.com/terms-of-service" target="_blank" class="underline hover:text-foreground">terms of service</a>
        and
        <a href="https://www.earthpbc.com/privacy-policy" target="_blank" class="underline hover:text-foreground">privacy policy</a>.
      </p>
    </div>
    <!-- Already have an account -->
    <div v-if="!signUpSuccess && !props.localToggle" class="text-sm text-center">
      <span class="text-muted-foreground">Already have an account? </span>
      <Button variant="link" class="p-0 h-auto" @click="goToSignIn">Sign in</Button>
    </div>
  </div>
</template>
